import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface Message {
  id: number;
  conversation_id: number;
  sender_id: number;
  content: string;
  attachment?: string;
  is_read: boolean;
  created_at: string;
}

export interface Conversation {
  id: number;
  user1_id: number;
  user2_id: number;
  job_id?: number;
  updated_at: string;
  created_at: string;
  user1_name?: string;
  user2_name?: string;
  other_user_name?: string;
  last_message?: string;
  last_message_at?: string;
  unread_count?: number;
}

export interface MessagesState {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  error: string | null;
}

const initialState: MessagesState = {
  conversations: [],
  currentConversation: null,
  messages: [],
  loading: false,
  error: null,
};

export const fetchConversations = createAsyncThunk(
  'messages/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/messages/conversations');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch conversations');
      }
      return rejectWithValue('Failed to fetch conversations');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async (conversationId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/messages/${conversationId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch messages');
      }
      return rejectWithValue('Failed to fetch messages');
    }
  }
);

export const markMessagesAsRead = createAsyncThunk(
  'messages/markMessagesAsRead',
  async (conversationId: number, { rejectWithValue }) => {
    try {
      await api.put(`/messages/${conversationId}/read`);
      return conversationId;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to mark as read');
      }
      return rejectWithValue('Failed to mark as read');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async (
    data: { conversationId: number; content: string; attachment?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/messages', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to send message');
      }
      return rejectWithValue('Failed to send message');
    }
  }
);

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      // If viewing this conversation, mark the incoming message as read locally
      const msg = { ...action.payload };
      if (state.currentConversation && msg.conversation_id === state.currentConversation.id) {
        msg.is_read = true;
      }
      state.messages.push(msg);
    },
    markConversationRead: (state, action: PayloadAction<number>) => {
      const conv = state.conversations.find(c => c.id === action.payload);
      if (conv) {
        conv.unread_count = 0;
      }
    },
    incrementUnreadCount: (state, action: PayloadAction<{ conversationId: number; lastMessage: string; timestamp: string }>) => {
      const conv = state.conversations.find(c => c.id === action.payload.conversationId);
      if (conv) {
        conv.unread_count = (conv.unread_count ?? 0) + 1;
        conv.last_message = action.payload.lastMessage;
        conv.last_message_at = action.payload.timestamp;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload.messages || [];
        state.currentConversation = action.payload.conversation;
        // Backend auto-marks messages as read on fetch — sync the conversation list
        const conv = state.conversations.find(c => c.id === action.payload.conversation?.id);
        if (conv) {
          conv.unread_count = 0;
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        const conv = state.conversations.find(c => c.id === action.payload);
        if (conv) {
          conv.unread_count = 0;
        }
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.messages.push(action.payload);
        // Update conversation sidebar with the sent message
        const conv = state.conversations.find(c => c.id === action.payload.conversation_id);
        if (conv) {
          conv.last_message = action.payload.content;
          conv.last_message_at = action.payload.created_at;
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { addMessage, markConversationRead, incrementUnreadCount } = messagesSlice.actions;
export default messagesSlice.reducer;
